import React, { useState, useEffect } from "react";
import axios from "axios";

const colors = {
  mainBlue: "#061941",
  gold: "#F2AF29",
  isabelline: "#F4F3EE",
  carrieban: "#3C6E71",
  cafeNoir: "#583E23",
};

export default function StudentCard({
  student,
  onView,
  onToggleSave,
  onContact,
}) {
  const [badges, setBadges] = useState([]);
  const [loadingBadges, setLoadingBadges] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const userId = userData.id || userData._id;

  const handleResumeClick = (e) => {
    e.stopPropagation();
    if (!student.resumeKey) {
      return alert("No CV available");
    }
    axios
      .get(`${process.env.REACT_APP_FASTAPI_URL}/get-resume-link`, {
        params: { file_key: student.resumeKey },
      })
      .then((res) => {
        window.open(res.data.url, "_blank");
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to fetch CV");
      });
  };

  // Fetch student badges
  useEffect(() => {
    (async () => {
      setLoadingBadges(true);
      try {
        if (!student._id) throw new Error();

        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/badges/user/${student._id}`
        );
        const all = await resp.json();

        const selIds = student.selectedBadges || [];

        // Fix: Extract IDs from selectedBadges if they're objects
        const selectedBadgeIds = selIds.map((badge) =>
          typeof badge === "string" ? badge : badge._id || badge.id
        );

        const filteredBadges = all.filter(
          (b) => b.badgeId?._id && selectedBadgeIds.includes(b.badgeId._id)
        );

        setBadges(filteredBadges);
      } catch {
        setBadges([]);
      } finally {
        setLoadingBadges(false);
      }
    })();
  }, [student._id, student.selectedBadges]);

  // Fetch saved state
  useEffect(() => {
    if (!userId || !student._id) return;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/saved/${userId}`)
      .then((res) => {
        const savedIds = res.data.savedItems.map((i) => i._id);
        setIsSaved(savedIds.includes(student._id));
      })
      .catch(() => {});
  }, [student._id, userId]);

  // Toggle save/unsave
  const handleHeartClick = (e) => {
    e.stopPropagation();
    if (!userId || !student._id) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/saved/${userId}/${student._id}`;
    const req = isSaved ? axios.delete(url) : axios.post(url);
    req
      .then(() => {
        const now = !isSaved;
        setIsSaved(now);
        if (!now && onToggleSave) onToggleSave(student._id);
      })
      .catch((err) => console.error(err));
  };

  const getBadgeIcon = (icon) => {
    switch (icon) {
      case "star":
        return "⭐";
      case "trophy":
        return "🏆";
      case "medal":
        return "🏅";
      case "fire":
        return "🔥";
      case "heart":
        return "❤️";
      case "crown":
        return "👑";
      case "gem":
        return "💎";
      case "rocket":
        return "🚀";
      default:
        return "🏅";
    }
  };

  // Helper functions to get institution and department display with levels
  const getInstitutionDisplay = () => {
    if (!student.education || student.education.length === 0) {
      return student.institution || "Institution";
    }

    if (student.education.length === 1) {
      const edu = student.education[0];
      const levelLabel = edu.level === "masters" ? "MS" : "BS";
      return `${edu.institution} (${levelLabel})`;
    }

    // Multiple education entries - show all with their levels on separate lines
    return student.education.map((edu) => {
      const levelLabel = edu.level === "masters" ? "MS" : "BS";
      return `${edu.institution} (${levelLabel})`;
    });
  };

  const getDepartmentDisplay = () => {
    if (!student.education || student.education.length === 0) {
      return student.department || "N/A";
    }

    if (student.education.length === 1) {
      const edu = student.education[0];
      const levelLabel = edu.level === "masters" ? "MS" : "BS";
      return `${edu.department} (${levelLabel})`;
    }

    // Multiple education entries - show all with their levels on separate lines
    return student.education.map((edu) => {
      const levelLabel = edu.level === "masters" ? "MS" : "BS";
      return `${edu.department} (${levelLabel})`;
    });
  };

  const gpa =
    student.gpa && student.gpaScale
      ? `${student.gpa}/${student.gpaScale}`
      : "N/A";
  const greScores =
    student.greVerbal || student.greQuantitative || student.greWriting
      ? [
          { label: "V", value: student.greVerbal || "N/A" },
          { label: "Q", value: student.greQuantitative || "N/A" },
          { label: "W", value: student.greWriting || "N/A" },
        ]
      : null;
  const interests =
    student.interests?.slice(0, 3).join(", ") +
      (student.interests?.length > 3 ? "..." : "") || "N/A";

  const institutionDisplay = getInstitutionDisplay();
  const departmentDisplay = getDepartmentDisplay();

  return (
    <div
      onClick={onView}
      className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
      style={{ border: `1px solid ${colors.mainBlue}15`, maxWidth: 340 }}
    >
      {/* Heart Icon */}
      <button
        onClick={handleHeartClick}
        className="absolute top-2 right-2 z-10 focus:outline-none"
        aria-label={isSaved ? "Unsave student" : "Save student"}
      >
        {isSaved ? (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill={colors.gold}
            stroke={colors.gold}
            strokeWidth="2"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        ) : (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke={colors.gold}
            strokeWidth="2"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        )}
      </button>

      {/* Header */}
      <div className="px-4 py-3" style={{ backgroundColor: colors.mainBlue }}>
        <div className="text-center">
          <h3 className="text-lg font-bold text-white mb-1">{student.name}</h3>
          <div className="text-xs text-white opacity-90">
            {Array.isArray(institutionDisplay) ? (
              institutionDisplay.map((institution, index) => (
                <div key={index}>{institution}</div>
              ))
            ) : (
              <div>{institutionDisplay}</div>
            )}
          </div>
        </div>
      </div>

      {/* GPA/GRE */}
      <div className="px-4 py-2" style={{ backgroundColor: colors.isabelline }}>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-0.5">GPA</p>
            <p className="text-sm font-bold text-gray-600">
              {(() => {
                if (!student.education || student.education.length === 0)
                  return "N/A";
                const mostRecentEntry =
                  student.education[student.education.length - 1];
                if (!mostRecentEntry || !mostRecentEntry.gpa) return "N/A";
                // Format GPA to remove unnecessary trailing zeros
                const formatGpa = (gpa) => {
                  return parseFloat(gpa).toString();
                };
                const formattedGpa = formatGpa(mostRecentEntry.gpa);
                const scale = mostRecentEntry.gpaScale || "4.0";
                return `${formattedGpa}/${scale}`;
              })()}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-0.5">GRE</p>
            {greScores ? (
              greScores.map((s) => (
                <div
                  key={s.label}
                  className="flex justify-center gap-1 text-xs"
                >
                  <span className="font-medium">{s.label}:</span> {s.value}
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-600">N/A</p>
            )}
          </div>
        </div>
      </div>

      {/* Department */}
      <div
        className="px-4 py-2 border-t"
        style={{ backgroundColor: colors.isabelline }}
      >
        <div className="space-y-1">
          <p
            className="text-xs font-semibold"
            style={{ color: colors.mainBlue }}
          >
            Department
          </p>
          <div className="text-xs text-gray-700">
            {Array.isArray(departmentDisplay) ? (
              departmentDisplay.map((department, index) => (
                <div key={index}>{department}</div>
              ))
            ) : (
              <div>{departmentDisplay}</div>
            )}
          </div>
        </div>
      </div>

      {/* Research Interests */}
      <div className="px-4 py-2">
        <div className="space-y-1">
          <p
            className="text-xs font-semibold"
            style={{ color: colors.mainBlue }}
          >
            Research Interests
          </p>
          <p className="text-xs text-gray-700 line-clamp-2">{interests}</p>
        </div>
      </div>

      {/* Badges */}
      <div className="px-4 py-2 border-t">
        {loadingBadges ? (
          <p className="text-center text-xs text-gray-500">Loading badges…</p>
        ) : badges.length ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span
                className="text-xs font-semibold"
                style={{ color: colors.mainBlue }}
              >
                Featured Badges
              </span>
              <div
                className="h-px flex-1"
                style={{ backgroundColor: `${colors.mainBlue}20` }}
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {badges.map((b) => (
                <div
                  key={b._id}
                  className="flex items-center rounded-full px-1.5 py-0.5 text-xs transition-transform hover:scale-105"
                  style={{
                    backgroundColor: `${b.badgeId?.color || "#ccc"}15`,
                    border: `1px solid ${b.badgeId?.color || "#ccc"}40`,
                  }}
                  title={`${b.badgeId?.name}: ${b.badgeId?.description}`}
                >
                  <span className="mr-0.5">
                    {getBadgeIcon(b.badgeId?.icon)}
                  </span>
                  <span style={{ color: b.badgeId?.color || "#666" }}>
                    {b.badgeId?.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-center text-xs text-gray-400">No badges</p>
        )}
      </div>

      {/* Contact + C.V. Buttons */}
      <div className="px-4 pb-3 flex gap-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (onContact) {
              onContact();
            }
          }}
          className="flex-1 py-2 rounded-lg text-white font-semibold text-sm"
          style={{ backgroundColor: colors.mainBlue }}
        >
          Contact
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleResumeClick(e);
          }}
          disabled={!student.resumeKey}
          className="flex-1 py-2 rounded-lg text-white font-semibold text-sm disabled:opacity-50"
          style={{ backgroundColor: colors.gold }}
        >
          C.V.
        </button>
        {student.linkedinUrl && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              window.open(student.linkedinUrl, "_blank");
            }}
            className="px-2 py-2 rounded-lg text-white font-semibold"
            style={{ backgroundColor: "#0077B5" }}
            title="View LinkedIn Profile"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
